import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { User, Trophy, TrendingUp, Clock } from 'lucide-react';

interface Nomination {
  id: string;
  player_name: string;
  player_username: string | null;
  sport: string;
  nominator_user_id: string | null;
  nominator_user_type: string | null;
  nominator_relationship: string | null;
  status: string;
  created_at: string;
}

interface Props {
  nominations: Nomination[];
}

export default function NominationAnalytics({ nominations }: Props) {
  // Group nominations by player
  const playerGroups = nominations.reduce((acc, nom) => {
    const key = nom.player_username || nom.player_name;
    if (!acc[key]) {
      acc[key] = { name: nom.player_name, username: nom.player_username, sport: nom.sport, nominations: [] };
    }
    acc[key].nominations.push(nom);
    return acc;
  }, {} as Record<string, { name: string; username: string | null; sport: string; nominations: Nomination[] }>);

  // Sort by nomination count descending
  const sortedPlayers = Object.values(playerGroups).sort((a, b) => b.nominations.length - a.nominations.length);

  // Unique nominators
  const uniqueNominators = new Set(nominations.filter(n => n.nominator_user_id).map(n => n.nominator_user_id)).size;

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6 text-center">
            <p className="text-3xl font-bold text-primary">{nominations.length}</p>
            <p className="text-sm text-muted-foreground">Total Nominations</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6 text-center">
            <p className="text-3xl font-bold text-primary">{sortedPlayers.length}</p>
            <p className="text-sm text-muted-foreground">Unique Players</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6 text-center">
            <p className="text-3xl font-bold text-primary">{uniqueNominators}</p>
            <p className="text-sm text-muted-foreground">Unique Nominators</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6 text-center">
            <p className="text-3xl font-bold text-primary">{nominations.filter(n => n.status === 'approved').length}</p>
            <p className="text-sm text-muted-foreground">Approved</p>
          </CardContent>
        </Card>
      </div>

      {/* Player Leaderboard */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <TrendingUp className="h-5 w-5 text-primary" />
            Most Nominated Players (Popularity & Traffic Indicators)
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {sortedPlayers.map((player, idx) => (
              <div key={idx} className="border border-border rounded-lg p-4">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center text-sm font-bold text-primary">
                      {idx + 1}
                    </div>
                    <div>
                      <p className="font-semibold text-foreground flex items-center gap-2">
                        <User className="h-4 w-4" />
                        {player.name}
                        {player.username && <span className="text-sm text-muted-foreground">{player.username}</span>}
                      </p>
                      <p className="text-xs text-muted-foreground flex items-center gap-1">
                        <Trophy className="h-3 w-3" /> {player.sport}
                      </p>
                    </div>
                  </div>
                  <Badge className="bg-primary text-primary-foreground text-lg px-3">
                    {player.nominations.length} nomination{player.nominations.length > 1 ? 's' : ''}
                  </Badge>
                </div>

                {/* Nomination timeline/log */}
                <div className="ml-11 space-y-2">
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Nomination Log:</p>
                  {player.nominations.map((nom, i) => (
                    <div key={nom.id} className="flex items-start gap-2 text-xs text-muted-foreground bg-muted/30 p-2 rounded">
                      <Clock className="h-3 w-3 mt-0.5 shrink-0" />
                      <div>
                        <span className="font-medium">{new Date(nom.created_at).toLocaleString()}</span>
                        <span className="mx-1">—</span>
                        <span>By: {nom.nominator_user_id ? `${nom.nominator_user_type} user (${nom.nominator_user_id.substring(0, 12)}...)` : 'Anonymous'}</span>
                        {nom.nominator_relationship && <span className="ml-1">({nom.nominator_relationship})</span>}
                        <span className="mx-1">•</span>
                        <Badge variant="outline" className="text-[10px] px-1 py-0">
                          {nom.status || 'pending'}
                        </Badge>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
